// ClientDocuments.jsx — shows the client's uploaded chart documents (the AR
// migration put ~7,782 PDFs in the PRIVATE `client-documents` bucket, keyed by
// client id: `<id>/<id>/<id>_<date>_<Type>_..._.pdf`). Lists them and opens
// each via a short-lived signed URL (bucket is private; staff-read policy added
// in migration 20260664).
import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  card:"#ffffff", border:"#e8e4df", borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", green:"#2e8b57", red:"#c0392b", blue:"#2563eb",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";
const BUCKET = "client-documents";

export default function ClientDocuments({ clientId }) {
  const [files, setFiles] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => { if (clientId != null) load(); /* eslint-disable-next-line */ }, [clientId]);

  const load = async () => {
    setFiles(null); setErr("");
    // Files live under "<clientId>/<ar_id>/...". The AR id usually equals the
    // Elyon client id, but differs for ~112 migrated clients — so listing only
    // "<id>/<id>/" silently hid those clients' documents. Walk EVERY subfolder
    // under the client's root instead (plus any files sitting at the root).
    const { data: top, error } = await supabase.storage.from(BUCKET).list(`${clientId}`, { limit: 1000 });
    if (error) { setErr(error.message); setFiles([]); return; }
    const out = [];
    const subdirs = [];
    for (const entry of top || []) {
      if (!entry.name || entry.name.startsWith(".")) continue;
      if (entry.id) out.push({ ...entry, fullPath: `${clientId}/${entry.name}` });
      else subdirs.push(entry.name);
    }
    for (const sub of subdirs) {
      const { data: inner, error: subErr } = await supabase.storage.from(BUCKET).list(`${clientId}/${sub}`, { limit: 1000 });
      if (subErr) { setErr(subErr.message); continue; }
      for (const f of inner || []) {
        if (f.id && f.name && !f.name.startsWith(".")) out.push({ ...f, fullPath: `${clientId}/${sub}/${f.name}` });
      }
    }
    // Newest first — filenames start with the document date.
    out.sort((a, b) => (a.name < b.name ? 1 : a.name > b.name ? -1 : 0));
    setFiles(out);
  };

  const open = async (fullPath) => {
    setBusy(fullPath); setErr("");
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(fullPath, 3600);
    setBusy("");
    if (error || !data?.signedUrl) { setErr("Could not open document: " + (error?.message || "unknown")); return; }
    window.open(data.signedUrl, "_blank", "noopener");
  };

  // Parse "<id>_2025-08-19-192504_Facial_3763_hash.pdf" → { date, label }
  const parse = (name) => {
    const m = name.match(/^\d+_(\d{4}-\d{2}-\d{2})-\d+_(.+?)(?:_\d+_[0-9a-f]+)?\.(pdf|png|jpe?g)$/i);
    if (m) return { date: m[1], label: m[2].replace(/_/g, " ").trim() };
    const m2 = name.match(/(\d{4}-\d{2}-\d{2})/);
    return { date: m2 ? m2[1] : "", label: name.replace(/\.(pdf|png|jpe?g)$/i, "").replace(/_/g, " ") };
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:8 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:C.gold, fontFamily:S }}>📄 Documents</div>
          <div style={{ fontSize:11, color:C.muted }}>Charts, forms &amp; records imported from AestheticRecord{files ? ` · ${files.length} file${files.length===1?"":"s"}` : ""}</div>
        </div>
        <button onClick={load} style={{ padding:"6px 14px", background:C.goldBg, color:C.gold, border:"1px solid "+C.borderGold, borderRadius:7, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:F }}>↻ Refresh</button>
      </div>

      {err && <div style={{ padding:"10px 14px", background:"rgba(192,57,43,0.1)", color:C.red, border:"1px solid rgba(192,57,43,0.25)", borderRadius:8, fontSize:12, marginBottom:12 }}>{err}</div>}

      {files === null ? (
        <div style={{ padding:30, textAlign:"center", color:C.muted, fontSize:12 }}>Loading documents…</div>
      ) : files.length === 0 ? (
        <div style={{ padding:26, textAlign:"center", color:C.dim, fontSize:12, background:C.light, borderRadius:9, border:"1px dashed "+C.border }}>
          No documents on file for this client.
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
          {files.map(f => {
            const { date, label } = parse(f.name);
            const isImg = /\.(png|jpe?g)$/i.test(f.name);
            return (
              <div key={f.fullPath} style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 14px", background:C.card, border:"1px solid "+C.border, borderLeft:"4px solid "+C.gold, borderRadius:9 }}>
                <div style={{ fontSize:22 }}>{isImg ? "🖼️" : "📄"}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{label || f.name}</div>
                  <div style={{ fontSize:11, color:C.muted }}>{date || "—"}{f.metadata?.size ? ` · ${(f.metadata.size/1024).toFixed(0)} KB` : ""}</div>
                </div>
                <button onClick={()=>open(f.fullPath)} disabled={busy===f.fullPath} style={{ padding:"7px 14px", background:busy===f.fullPath?C.light:"linear-gradient(135deg,"+C.gold+",#8a6f2e)", color:busy===f.fullPath?C.muted:"#fff", border:"none", borderRadius:7, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:F, whiteSpace:"nowrap" }}>
                  {busy===f.fullPath ? "Opening…" : "View"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
